import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, FileText, Edit2, Trash2, Calendar, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSession } from '../../contexts/SessionContext';
import RightSlideModal from '../../components/RightSlideModal';

export default function ExamsPage() {
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});
  const [loading, setLoading] = useState(true);
  const [showExamModal, setShowExamModal] = useState(false);
  const [showMarksModal, setShowMarksModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const { selectedSession, sessions } = useSession();
  const [selectedClass, setSelectedClass] = useState('all');
  const [modalSession, setModalSession] = useState('');
  const [examForm, setExamForm] = useState({ examName: '', classId: '', startDate: '', endDate: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [examsRes, classesRes, subjectsRes] = await Promise.all([
        api.get('/exams'),
        api.get('/classes'),
        api.get('/subjects')
      ]);
      setExams(examsRes.data);
      setClasses(classesRes.data);
      setSubjects(subjectsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleExamSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedExam) {
        await api.put(`/exams/${selectedExam.id}`, examForm);
        toast.success('Exam updated successfully');
      } else {
        await api.post('/exams', examForm);
        toast.success('Exam created successfully');
      }
      setShowExamModal(false);
      setSelectedExam(null);
      setExamForm({ examName: '', classId: '', startDate: '', endDate: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save exam');
    }
  };

  const handleDeleteExam = async (id) => {
    if (!window.confirm('Delete this exam and all associated marks?')) return;
    try {
      await api.delete(`/exams/${id}`);
      toast.success('Exam deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete exam');
    }
  };

  const handleEnterMarks = async (exam) => {
    setSelectedExam(exam);
    try {
      const [studentsRes, marksRes] = await Promise.all([
        api.get(`/students?classId=${exam.classId}`),
        api.get(`/marks?examId=${exam.id}`)
      ]);
      setStudents(studentsRes.data);
      
      const marksMap = {};
      marksRes.data.forEach(mark => {
        const key = `${mark.studentId}-${mark.subjectId}`;
        marksMap[key] = { marks: mark.marks, maxMarks: mark.maxMarks, remarks: mark.remarks };
      });
      setMarks(marksMap);
      setShowMarksModal(true);
    } catch (error) {
      toast.error('Failed to fetch marks data');
    }
  };

  const handleMarkChange = (studentId, subjectId, field, value) => {
    const key = `${studentId}-${subjectId}`;
    setMarks(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  };

  const handleSaveMarks = async () => {
    if (!selectedSubject) {
      toast.error('Please select a subject');
      return;
    }

    try {
      const marksList = students.map(student => {
        const key = `${student.id}-${selectedSubject}`;
        const mark = marks[key] || {};
        return {
          studentId: student.id,
          marks: parseFloat(mark.marks) || 0,
          maxMarks: parseFloat(mark.maxMarks) || 100,
          remarks: mark.remarks || ''
        };
      });

      await api.post('/marks/bulk', {
        examId: selectedExam.id,
        subjectId: selectedSubject,
        marksList
      });
      
      toast.success('Marks saved successfully');
      setShowMarksModal(false);
      setSelectedExam(null);
      setSelectedSubject('');
      setMarks({});
    } catch (error) {
      toast.error('Failed to save marks');
    }
  };

  const getExamSubjects = (classId) => {
    return subjects.filter(s => s.classId === classId);
  };

  const getStatus = (start, end) => {
    const now = new Date();
    const s = new Date(start);
    const e = new Date(end);
    if (now < s) return { label: 'Upcoming', color: 'bg-blue-100 text-blue-700' };
    if (now > e) return { label: 'Completed', color: 'bg-green-100 text-green-700' };
    return { label: 'Ongoing', color: 'bg-orange-100 text-orange-700' };
  };

  const filteredExams = exams.filter(exam => {
    const examClass = classes.find(c => c.id === exam.classId);
    
    // If selectedSession is specified, check against class session. 
    // If examClass has no session, show it if selectedSession is empty or if we want to be more permissive.
    const sessionMatch = !selectedSession || selectedSession === 'all' || examClass?.sessionYear === selectedSession;
    const classMatch = selectedClass === 'all' || exam.classId === selectedClass;
    return sessionMatch && classMatch;
  });

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="font-outfit font-semibold text-2xl text-slate-800">Exam Schedules</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${selectedSession === 'all' ? 'bg-slate-100 text-slate-600' : 'bg-indigo-50 text-indigo-700'}`}>
                Session: {selectedSession === 'all' ? 'All Sessions' : selectedSession}
              </span>
              <div className="h-4 w-px bg-slate-200" />
              <select 
                value={selectedClass} 
                onChange={(e) => setSelectedClass(e.target.value)}
                className="bg-transparent border-none text-xs font-semibold text-slate-500 focus:ring-0 cursor-pointer"
              >
                <option value="all">All Classes</option>
                {classes.filter(c => selectedSession === 'all' || c.sessionYear === selectedSession).map(c => (
                  <option key={c.id} value={c.id}>{c.className}</option>
                ))}
              </select>
            </div>
          </div>
          <button onClick={() => { 
            setSelectedExam(null); 
            setExamForm({ examName: '', classId: '', startDate: '', endDate: '' }); 
            setModalSession(selectedSession === 'all' ? '' : selectedSession);
            setShowExamModal(true); 
          }} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-2.5 font-medium shadow-sm transition-all flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Schedule Exam
          </button>
        </div>

      {filteredExams.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredExams.map(exam => {
            const examClass = classes.find(c => c.id === exam.classId);
            const status = getStatus(exam.startDate, exam.endDate);
            return (
              <div key={exam.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden group hover:border-indigo-200 transition-all">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                      <h3 className="font-outfit font-semibold text-lg text-slate-800 mt-2">{exam.examName}</h3>
                      <p className="text-sm font-medium text-indigo-600">{examClass?.className} <span className="text-slate-300 mx-1">|</span> {examClass?.sessionYear || 'No Session'}</p>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => { 
                          setSelectedExam(exam); 
                          setExamForm({ examName: exam.examName, classId: exam.classId, startDate: exam.startDate, endDate: exam.endDate }); 
                          const examClass = classes.find(c => c.id === exam.classId);
                          setModalSession(examClass?.sessionYear || '');
                          setShowExamModal(true); 
                        }}
                        className="p-1.5 text-slate-400 hover:text-indigo-600 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteExam(exam.id)}
                        className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-2 bg-slate-50 rounded-xl">
                      <p className="text-[10px] text-slate-400 uppercase font-bold px-1">Starts</p>
                      <p className="text-sm font-semibold text-slate-700 px-1">{new Date(exam.startDate).toLocaleDateString()}</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-xl">
                      <p className="text-[10px] text-slate-400 uppercase font-bold px-1">Ends</p>
                      <p className="text-sm font-semibold text-slate-700 px-1">{new Date(exam.endDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <button onClick={() => handleEnterMarks(exam)} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-2.5 text-sm font-medium transition-all shadow-sm active:scale-95 flex items-center justify-center gap-2">
                    <FileText className="w-4 h-4" />
                    Enter Marks
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-dashed border-slate-200 py-16 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="font-outfit font-semibold text-slate-800">No exams scheduled</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">There are no exams listed for the selected filters. Schedule a new one to get started.</p>
        </div>
      )}

      <RightSlideModal 
        isOpen={showExamModal} 
        onClose={() => setShowExamModal(false)} 
        title="Create Exam"
      >
        <form onSubmit={handleExamSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Exam Name</label>
            <input type="text" value={examForm.examName} onChange={(e) => setExamForm({...examForm, examName: e.target.value})} placeholder="e.g., Mid Term Exam" className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Academic Session</label>
              <select 
                value={modalSession} 
                onChange={(e) => {
                  setModalSession(e.target.value);
                  setExamForm({...examForm, classId: ''});
                }} 
                className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" 
                required
              >
                <option value="">Select Session</option>
                {sessions.map(s => <option key={s.id} value={s.sessionYear}>{s.sessionYear}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Class</label>
              <select 
                value={examForm.classId} 
                onChange={(e) => setExamForm({...examForm, classId: e.target.value})} 
                className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" 
                required 
                disabled={!modalSession}
              >
                <option value="">Select Class</option>
                {classes.filter(c => c.sessionYear === modalSession).map(c => (
                  <option key={c.id} value={c.id}>{c.className}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
              <input type="date" value={examForm.startDate} onChange={(e) => setExamForm({...examForm, startDate: e.target.value})} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">End Date</label>
              <input type="date" value={examForm.endDate} onChange={(e) => setExamForm({...examForm, endDate: e.target.value})} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" required />
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-2.5 font-medium">Create Exam</button>
            <button type="button" onClick={() => setShowExamModal(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 rounded-full py-2.5 font-medium">Cancel</button>
          </div>
        </form>
      </RightSlideModal>

      <RightSlideModal 
        isOpen={showMarksModal && !!selectedExam} 
        onClose={() => setShowMarksModal(false)} 
        title={selectedExam ? `Enter Marks - ${selectedExam.examName}` : 'Enter Marks'}
        maxWidth="max-w-xl"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Select Subject</label>
            <select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" required>
              <option value="">Select Subject</option>
              {selectedExam && getExamSubjects(selectedExam.classId).map(s => <option key={s.id} value={s.id}>{s.subjectName}</option>)}
            </select>
          </div>

          {selectedSubject && (
            <div className="space-y-3">
              {students.map(student => {
                const key = `${student.id}-${selectedSubject}`;
                const mark = marks[key] || {};
                return (
                  <div key={student.id} className="p-4 bg-slate-50 rounded-xl">
                    <p className="font-medium text-slate-800 mb-2">{student.name} (Roll: {student.rollNumber})</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Marks Obtained</label>
                        <input type="number" value={mark.marks || ''} onChange={(e) => handleMarkChange(student.id, selectedSubject, 'marks', e.target.value)} placeholder="0" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Max Marks</label>
                        <input type="number" value={mark.maxMarks || ''} onChange={(e) => handleMarkChange(student.id, selectedSubject, 'maxMarks', e.target.value)} placeholder="100" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Remarks</label>
                        <input type="text" value={mark.remarks || ''} onChange={(e) => handleMarkChange(student.id, selectedSubject, 'remarks', e.target.value)} placeholder="Good" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="pt-6 flex gap-3 sticky bottom-0 bg-white border-t border-slate-100">
            <button onClick={handleSaveMarks} disabled={!selectedSubject} className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-full py-3 font-semibold shadow-lg shadow-indigo-200 transition-all active:scale-95">Save All Marks</button>
            <button onClick={() => setShowMarksModal(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 rounded-full py-3 font-semibold text-slate-600 transition-all">Cancel</button>
          </div>
        </div>
      </RightSlideModal>
    </div>
  );
}
